import React from "react"
import "../styles.css"
import PaymentModal from "../components/PaymentModal"
import axios from "axios"
import { toast } from "react-toastify"
import {
  cacheProfileToLocal,
  clearCachedProfile,
  shouldShowDepositWarning,
} from "../utils/profileCache"

const InvestmentChartSkeleton = () => (
  <div className="investment-chart-skeleton">
    <div className="title-skeleton skeleton" />
    <div className="value-skeleton skeleton" />
    <div className="sub-skeleton skeleton" />
    <div className="chart-skeleton skeleton" />
  </div>
)

const TransactionTableSkeleton = () => (
  <div id="transactions" className="transaction-table-skeleton">
    <div className="title-skeleton skeleton" />
    {[1, 2, 3, 4, 5].map((i) => (
      <div key={i} className="row-skeleton skeleton" />
    ))}
  </div>
)

const EmptyInvestmentChart = ({ onMakePayment }) => (
  <div className="empty-state" style={{ marginBottom: 32 }}>
    <div className="empty-state-icon">📈</div>
    <div className="empty-state-title">No Investment Data Yet</div>
    <div className="empty-state-description">
      Start your bitcoin investment journey today. Make your first payment to
      see your investment performance.
    </div>
    <button className="empty-state-action" onClick={onMakePayment}>
      Make Your First Payment
    </button>
  </div>
)

const EmptyTransactionTable = ({ onMakePayment }) => (
  <div className="empty-state" id="transactions">
    <div className="empty-state-icon">📝</div>
    <div className="empty-state-title">No Transactions Yet</div>
    <div className="empty-state-description">
      You haven't made any transactions yet. Start your investment journey by
      making your first payment.
    </div>
    <button className="empty-state-action" onClick={onMakePayment}>
      Make Your First Payment
    </button>
  </div>
)

const DepositAddressWarningModal = ({ onClose }) => (
  <div className="modal-overlay deposit-warning-overlay" onClick={onClose}>
    <div
      className="modal-content"
      style={{ maxWidth: 520 }}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        className="modal-close"
        onClick={onClose}
        aria-label="Close"
      >
        &times;
      </button>
      <div className="payment-content">
        <h2 className="payment-title" style={{ color: "#facc15" }}>
          Important: Crypto deposit addresses have changed
        </h2>
        <p style={{ color: "#b0b8c1", lineHeight: 1.6, margin: 0 }}>
          We have moved away from exchange-domiciled wallets to give us greater
          control over incoming funds. Please use the updated addresses shown
          on your payment page.
        </p>
      </div>
    </div>
  </div>
)

const usd = (n) =>
  `$${Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`

// Smoothly counts a number up from 0 → target on mount (ease-out cubic).
const AnimatedNumber = ({ value, format, duration = 950 }) => {
  const [display, setDisplay] = React.useState(0)

  React.useEffect(() => {
    const to = Number(value) || 0
    let raf
    const start = performance.now()
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration)
      const eased = 1 - Math.pow(1 - t, 3)
      setDisplay(to * eased)
      if (t < 1) raf = requestAnimationFrame(tick)
      else setDisplay(to)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [value, duration])

  return <>{format ? format(display) : Math.round(display).toLocaleString()}</>
}

const PortfolioOverview = ({ data, onMakePayment, liveBtcPrice }) => {
  const totalDeposit = Number(data?.totalDeposit) || 0
  const btc = Number(data?.balance) || 0

  // Value the holding at the live BTC/USD rate when we have it
  // (live rate × BTC owned). Fall back to the server-provided present
  // value whenever the live rate isn't available.
  const apiPresentValue = Number(data?.presentValue) || 0
  const hasLiveRate = Number.isFinite(liveBtcPrice) && liveBtcPrice > 0
  const presentValue = hasLiveRate ? liveBtcPrice * btc : apiPresentValue
  const profit = presentValue - totalDeposit
  const profitPct = totalDeposit > 0 ? (profit / totalDeposit) * 100 : 0
  const up = profit >= 0

  return (
    <>
      <div className="bento">
        <div className="hero-panel">
          <div className="hero-panel-glow" />
          <div className="hero-eyebrow">Portfolio Value</div>
          <div className="hero-value">
            <AnimatedNumber value={presentValue} format={usd} />
          </div>
          <div className={`hero-pl ${up ? "up" : "down"}`}>
            {up ? "+" : "−"}
            {usd(Math.abs(profit))} ({up ? "+" : "−"}
            {Math.abs(profitPct).toFixed(2)}%)
            <span className="hero-pl-label">all-time</span>
          </div>
          <div className="hero-meta">
            <div className="hero-meta-item">
              <span>Total Deposit</span>
              <strong>{usd(totalDeposit)}</strong>
            </div>
            <div className="hero-meta-divider" />
            <div className="hero-meta-item">
              <span>Bitcoin Holding</span>
              <strong>{btc.toFixed(8)} BTC</strong>
            </div>
          </div>
        </div>

        <div
          className="cta-card"
          role="button"
          tabIndex={0}
          onClick={onMakePayment}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault()
              onMakePayment()
            }
          }}
        >
          <div className="cta-title">Grow your stack</div>
          <div className="cta-text">
            Add to your Bitcoin position in just a few taps.
          </div>
          <span className="cta-btn">
            Make Payment <span className="cta-arrow">→</span>
          </span>
        </div>
      </div>

      <div className="stat-tiles">
        <div className="stat-tile">
          <div className="stat-tile-label">Total Deposit</div>
          <div className="stat-tile-value">
            <AnimatedNumber value={totalDeposit} format={usd} />
          </div>
        </div>
        <div className="stat-tile">
          <div className="stat-tile-label">Bitcoin Holding</div>
          <div className="stat-tile-value">
            {btc.toFixed(8)} <span className="stat-tile-unit">BTC</span>
          </div>
        </div>
        <div className="stat-tile">
          <div className="stat-tile-label">Net Profit / Loss</div>
          <div
            className="stat-tile-value"
            style={{ color: up ? "var(--green)" : "var(--red)" }}
          >
            {up ? "+" : "−"}
            <AnimatedNumber value={Math.abs(profit)} format={usd} />
          </div>
        </div>
      </div>
    </>
  )
}

const InvestmentChart = ({ data, onMakePayment }) => {
  const [priceData, setPriceData] = React.useState(null)
  const [isLoading, setIsLoading] = React.useState(true)
  const [error, setError] = React.useState(null)

  const fetchBitcoinPrices = React.useCallback(async () => {
    let isMounted = true
    try {
      setError(null)
      setIsLoading(true)
      const response = await axios.get(
        "https://api.coingecko.com/api/v3/coins/bitcoin/market_chart",
        {
          params: {
            vs_currency: "usd",
            days: "365",
          },
        },
      )

      if (!isMounted) return

      // Transform the data to get monthly points
      const prices = response.data.prices
      const monthlyData = prices.reduce((acc, [timestamp, price]) => {
        const date = new Date(timestamp)
        const monthKey = `${date.getFullYear()}-${date.getMonth()}`

        if (!acc[monthKey]) {
          acc[monthKey] = {
            date,
            prices: [],
          }
        }

        acc[monthKey].prices.push(price)
        return acc
      }, {})

      // Get average price for each month
      const formattedData = Object.values(monthlyData).map(
        ({ date, prices }) => ({
          date,
          price: prices.reduce((sum, price) => sum + price, 0) / prices.length,
        }),
      )

      // Sort by date
      formattedData.sort((a, b) => a.date - b.date)

      // Get the current price and calculate percentage change
      const currentPrice = prices[prices.length - 1][1]
      const oldPrice = prices[0][1]
      const percentageChange = ((currentPrice - oldPrice) / oldPrice) * 100

      if (isMounted) {
        setPriceData({
          points: formattedData,
          value: `$${currentPrice.toLocaleString(undefined, {
            maximumFractionDigits: 2,
          })}`,
          sub: `${percentageChange >= 0 ? "+" : ""}${percentageChange?.toFixed(
            2,
          )}% in the last year`,
          isPositive: percentageChange >= 0,
        })
      }
    } catch (err) {
      console.error("Error fetching Bitcoin prices:", err)
      if (isMounted) {
        setError("Failed to load Bitcoin price data")
      }
    } finally {
      if (isMounted) {
        setIsLoading(false)
      }
    }

    return () => {
      isMounted = false
    }
  }, [])

  React.useEffect(() => {
    fetchBitcoinPrices()
  }, [fetchBitcoinPrices])

  if (isLoading) {
    return <InvestmentChartSkeleton />
  }

  if (error) {
    return (
      <div className="investment-chart-container">
        <div className="investment-chart-title">Bitcoin Price Chart</div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "16px",
            padding: "32px 0",
          }}
        >
          <div
            className="error-message"
            style={{
              color: "#ffffff",
              marginBottom: "8px",
            }}
          >
            {error}
          </div>
          <button
            onClick={fetchBitcoinPrices}
            style={{
              background: "none",
              border: "2px solid #23272f",
              borderRadius: "8px",
              padding: "8px 16px",
              color: "#fff",
              cursor: "pointer",
              transition: "all 0.2s",
            }}
            onMouseOver={(e) =>
              (e.target.style.background = "rgba(255, 255, 255, 0.1)")
            }
            onMouseOut={(e) => (e.target.style.background = "none")}
          >
            Refetch Data
          </button>
        </div>
      </div>
    )
  }

  if (!priceData) {
    return <EmptyInvestmentChart onMakePayment={onMakePayment} />
  }

  // Calculate chart points
  const points = priceData.points
  const maxPrice = Math.max(...points.map((p) => p.price))
  const minPrice = Math.min(...points.map((p) => p.price))
  const range = maxPrice - minPrice

  // Create SVG points string
  const coords = points.map((point, index) => {
    const x = (index / (points.length - 1)) * 300
    const y = 80 - ((point.price - minPrice) / range) * 60
    return { x, y }
  })
  const svgPoints = coords.map(({ x, y }) => `${x},${y}`).join(" ")
  // Closed polygon for the gradient area fill (line + down to the baseline)
  const areaPoints = `${svgPoints} 300,80 0,80`
  const lastPoint = coords[coords.length - 1]

  const trendColor = priceData.isPositive ? "#4ade80" : "#f87171"
  const gradId = priceData.isPositive ? "areaUp" : "areaDown"

  return (
    <div className="investment-chart-container">
      <div className="investment-chart-title">Bitcoin Price History</div>
      <div className="investment-chart-value">{priceData.value}</div>
      <div className="investment-chart-sub" style={{ color: trendColor }}>
        {priceData.sub}
      </div>
      <svg width="100%" height="300" viewBox="0 0 300 80">
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={trendColor} stopOpacity="0.38" />
            <stop offset="100%" stopColor={trendColor} stopOpacity="0" />
          </linearGradient>
        </defs>
        <polygon
          className="chart-area"
          fill={`url(#${gradId})`}
          points={areaPoints}
        />
        <polyline
          className="chart-line"
          fill="none"
          stroke={trendColor}
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          pathLength="1"
          points={svgPoints}
        />
        {lastPoint && (
          <circle
            className="chart-dot"
            cx={lastPoint.x}
            cy={lastPoint.y}
            r="2.6"
            fill={trendColor}
            style={{ color: trendColor }}
          />
        )}
      </svg>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: 8,
          fontSize: "0.95rem",
          opacity: 0.7,
        }}
      >
        {priceData.points.map((point, i) => (
          <span key={i}>
            {point.date.toLocaleDateString("default", { month: "short" })}
          </span>
        ))}
      </div>
    </div>
  )
}

const formatCurrency = (value, medium) => {
  switch (medium?.toLowerCase()) {
    case "naira":
      return `₦${value.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`
    case "cad":
      return `CA$${value.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`
    case "crypto":
    case "usd":
    default:
      return `$${value.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`
  }
}

// Mediums the dashboard table renders. Admin-driven corrections
// (Liquidation, Transfer, Correction_usdt, Correction_btc) DO affect the
// member's balance but are intentionally hidden from this list. The BE
// already filters them out — this is a belt-and-braces guard so any
// future leak doesn't surface as a row with no recognizable medium.
const VISIBLE_MEDIUMS = new Set(["naira", "crypto", "usd", "cad"])

const TransactionTable = ({ transactions, onMakePayment }) => {
  const [sortOrder, setSortOrder] = React.useState("desc") // 'asc' or 'desc'

  const sortedTransactions = React.useMemo(() => {
    if (!transactions?.length) return []
    const visibleOnly = transactions.filter((t) =>
      VISIBLE_MEDIUMS.has((t.medium || "").toLowerCase()),
    )
    if (visibleOnly.length === 0) return []

    const monthOrder = {
      January: 0,
      February: 1,
      March: 2,
      April: 3,
      May: 4,
      June: 5,
      July: 6,
      August: 7,
      September: 8,
      October: 9,
      November: 10,
      December: 11,
    }

    return [...visibleOnly].sort((a, b) => {
      const yearA = a.year ? parseInt(a.year) : null
      const yearB = b.year ? parseInt(b.year) : null
      const monthA = monthOrder[a.month]
      const monthB = monthOrder[b.month]
      const createdA = a.created_at ? new Date(a.created_at) : null
      const createdB = b.created_at ? new Date(b.created_at) : null

      // Compare years first (if both have years)
      if (yearA !== null && yearB !== null && yearA !== yearB) {
        return sortOrder === "asc" ? yearA - yearB : yearB - yearA
      }

      // If one has year and other doesn't, prioritize the one with year
      if (yearA !== null && yearB === null) {
        return sortOrder === "asc" ? -1 : 1
      }
      if (yearA === null && yearB !== null) {
        return sortOrder === "asc" ? 1 : -1
      }

      // If same year (or both null), compare months
      if (monthA !== undefined && monthB !== undefined && monthA !== monthB) {
        return sortOrder === "asc" ? monthA - monthB : monthB - monthA
      }

      // If same year and month, prioritize records with created_at
      // (created_at IS NULL: false=0 comes before true=1)
      const hasCreatedA = createdA === null ? 1 : 0
      const hasCreatedB = createdB === null ? 1 : 0
      if (hasCreatedA !== hasCreatedB) {
        return hasCreatedA - hasCreatedB // 0 (has date) comes before 1 (no date)
      }

      // If both have created_at or both don't, sort by created_at DESC
      if (createdA !== null && createdB !== null) {
        return sortOrder === "asc" ? createdA - createdB : createdB - createdA
      }

      // If both are null, maintain original order (or sort by value as fallback)
      return sortOrder === "asc"
        ? (a.usdtValue || 0) - (b.usdtValue || 0)
        : (b.usdtValue || 0) - (a.usdtValue || 0)
    })
  }, [transactions, sortOrder])

  const toggleSort = () => {
    setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"))
  }

  if (!transactions?.length) {
    return <EmptyTransactionTable onMakePayment={onMakePayment} />
  }

  return (
    <div id="transactions" className="transaction-table-container">
      <div className="transaction-table-title">Transaction History</div>
      <table className="transaction-table">
        <thead>
          <tr>
            <th onClick={toggleSort} style={{ cursor: "pointer" }}>
              Date{" "}
              <span
                className={`sort-indicator ${
                  sortOrder === "asc" ? "asc" : "desc"
                }`}
              >
                &gt;
              </span>
            </th>
            <th>Amount Purchased (BTC)</th>
            <th>Deposit</th>
            <th>USD Value of Purchase</th>
          </tr>
        </thead>
        <tbody>
          {sortedTransactions?.map((t, i) => {
            const btcAmount =
              t.btcHolding != null ? Number(t.btcHolding) : null

            // direction = 'credit' | 'debit' from the API.  Falls back to
            // inspecting the sign of the value for older payloads.
            // Debits render with a leading "-", credits render bare.
            const apiDir = t.direction
            const inferredDebit =
              apiDir === "debit" ||
              (apiDir == null &&
                (Number(t.usdtValue) < 0 ||
                  (btcAmount != null && btcAmount < 0)))
            const sign = inferredDebit ? "-" : ""
            const signClass = inferredDebit ? "tx-debit" : "tx-credit"
            const magnitude = (x) =>
              x == null ? null : Math.abs(Number(x))

            return (
              <tr key={i} className={signClass}>
                <td>
                  {t.month}
                  {t.year ? `, ${t.year}` : ""}
                </td>
                <td>
                  {Number.isFinite(btcAmount)
                    ? `${sign}${Math.abs(btcAmount).toFixed(8)}`
                    : "-"}
                </td>
                <td>
                  {t.fiatValue != null
                    ? `${sign}${formatCurrency(magnitude(t.fiatValue), t.medium)}`
                    : "-"}
                </td>
                <td>
                  {t.usdtValue != null
                    ? `${sign}${formatCurrency(magnitude(t.usdtValue), "usd")}`
                    : "-"}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

const DashboardScreen = () => {
  const [isPaymentModalOpen, setIsPaymentModalOpen] = React.useState(false)
  const [isWarningModalOpen, setIsWarningModalOpen] = React.useState(false)
  const [balanceData, setBalanceData] = React.useState(null)
  const [transactionsData, setTransactionsData] = React.useState(null)
  const [isDashboardLoading, setIsDashboardLoading] = React.useState(true)
  const [isTransactionsLoading, setIsTransactionsLoading] = React.useState(true)
  const [liveBtcPrice, setLiveBtcPrice] = React.useState(null)
  const isMountedRef = React.useRef(true)

  const handleCloseWarningModal = React.useCallback(async () => {
    setIsWarningModalOpen(false)

    const token = localStorage.getItem("auth_key")
    const baseUrl = process.env.REACT_APP_BASE_URL
    if (!baseUrl || !token) return

    try {
      await axios.post(
        `${baseUrl}/profile/seen-deposit-warning`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
    } catch (error) {
      // Non-fatal: worst case the user sees the modal again next time.
      console.error("Error marking deposit warning as seen:", error)
    }
  }, [])

  const handleLogout = React.useCallback(() => {
    toast.info("Token expired, please log in again to see your holdings", {
      position: "top-right",
      autoClose: 3000,
    })
    // Small delay to allow the toast to be seen
    setTimeout(() => {
      localStorage.removeItem("auth_key")
      clearCachedProfile()
      window.location.href = "/"
    }, 1000)
  }, [])

  React.useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  // Fetch the live BTC/USD spot rate so the portfolio value reflects the
  // real-time price (live rate × BTC owned). Runs independently of the
  // balance request so a slow/failed rate call never blocks the dashboard;
  // when it can't be fetched, PortfolioOverview keeps the server-provided
  // present value as a failsafe.
  React.useEffect(() => {
    const controller = new AbortController()

    const fetchLiveBtcPrice = async () => {
      try {
        const response = await axios.get(
          "https://api.coingecko.com/api/v3/simple/price",
          {
            params: { ids: "bitcoin", vs_currencies: "usd" },
            signal: controller.signal,
          },
        )
        const price = Number(response?.data?.bitcoin?.usd)
        if (isMountedRef.current && Number.isFinite(price) && price > 0) {
          setLiveBtcPrice(price)
        }
      } catch (error) {
        if (!axios.isCancel(error)) {
          console.error("Failed to fetch live BTC price:", error)
        }
      }
    }

    fetchLiveBtcPrice()

    return () => controller.abort()
  }, [])

  const ensureProfileCached = React.useCallback(async () => {
    const token = localStorage.getItem("auth_key")
    const baseUrl = process.env.REACT_APP_BASE_URL
    if (!baseUrl || !token) return null

    try {
      const response = await axios.get(`${baseUrl}/profile`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      if (response?.data) {
        cacheProfileToLocal(response.data)
        if (
          shouldShowDepositWarning(response.data) &&
          isMountedRef.current
        ) {
          setIsWarningModalOpen(true)
        }
        return response.data
      }
    } catch (error) {
      console.error("Error caching profile:", error)
      if (error?.response?.status === 401 || error?.response?.status === 404) {
        handleLogout()
      }
    }
    return null
  }, [handleLogout])

  const fetchDashboardData = React.useCallback(async () => {
    const token = localStorage.getItem("auth_key")
    const baseUrl = process.env.REACT_APP_BASE_URL

    if (!baseUrl || !token) {
      handleLogout()
      return
    }

    setIsDashboardLoading(true)
    setIsTransactionsLoading(true)

    let balanceError = null
    let transactionsError = null

    try {
      await ensureProfileCached()

      const [balanceResult, transactionsResult] = await Promise.allSettled([
        axios.get(`${baseUrl}/balance`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }),
        axios.get(`${baseUrl}/transactions`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }),
      ])

      if (!isMountedRef.current) {
        return
      }

      if (balanceResult.status === "fulfilled") {
        const balanceResponse = balanceResult.value
        if (
          !balanceResponse.data?.balance &&
          balanceResponse.data?.balance !== 0
        ) {
          handleLogout()
          return
        }
        setBalanceData(balanceResponse.data)
      } else {
        balanceError = balanceResult.reason
        if (
          balanceError?.response?.status === 401 ||
          balanceError?.response?.status === 404
        ) {
          handleLogout()
          return
        }
        console.error("Error fetching balance:", balanceError)
      }

      if (transactionsResult.status === "fulfilled") {
        const transactionsResponse = transactionsResult.value
        setTransactionsData(transactionsResponse.data)
      } else {
        transactionsError = transactionsResult.reason
        if (
          transactionsError?.response?.status === 401 ||
          transactionsError?.response?.status === 404
        ) {
          handleLogout()
          return
        }
        console.error("Error fetching transactions:", transactionsError)
      }
    } catch (error) {
      console.error("Error fetching data:", error)
      if (error.response?.status === 401 || error.response?.status === 404) {
        handleLogout()
      }
    } finally {
      if (isMountedRef.current) {
        setIsDashboardLoading(false)
        setIsTransactionsLoading(false)
      }
    }
  }, [handleLogout, ensureProfileCached])

  React.useEffect(() => {
    fetchDashboardData()
  }, [fetchDashboardData])

  React.useEffect(() => {
    const scrollToTransactions = () => {
      if (window.location.hash === "#transactions") {
        const el = document.getElementById("transactions")
        if (el) {
          setTimeout(() => {
            el.scrollIntoView({ behavior: "smooth", block: "start" })
          }, 100)
        }
      }
    }
    scrollToTransactions()
    window.addEventListener("hashchange", scrollToTransactions)
    return () => window.removeEventListener("hashchange", scrollToTransactions)
  }, [])

  const handleMakePayment = () => setIsPaymentModalOpen(true)

  const firstName = (localStorage.getItem("user_first_name") || "").trim()
  const greeting = firstName ? `Welcome back, ${firstName}` : "Welcome back"

  return (
    <div className="dashboard-content">
      {isWarningModalOpen && (
        <DepositAddressWarningModal onClose={handleCloseWarningModal} />
      )}

      <div className="dash-header">
        <div className="dash-header-text">
          <h1 className="dash-hello">{greeting}</h1>
          <div className="dash-sub">
            Track your bitcoin holding and performance over time.
          </div>
        </div>
        <button
          className="make-payment-btn dash-header-cta"
          onClick={handleMakePayment}
        >
          Make Payment
        </button>
      </div>

      {isDashboardLoading ? (
        <>
          <div className="bento">
            <div className="hero-panel sk-panel">
              <div className="skeleton sk-line w30" />
              <div className="skeleton sk-line w60 sk-xl" />
              <div className="skeleton sk-line w40" />
            </div>
            <div className="cta-card sk-panel">
              <div className="skeleton sk-line w50" />
              <div className="skeleton sk-line w80" />
            </div>
          </div>
          <div className="stat-tiles">
            {[1, 2, 3].map((i) => (
              <div className="stat-tile sk-panel" key={i}>
                <div className="skeleton sk-line w50" />
                <div className="skeleton sk-line w70 sk-lg" />
              </div>
            ))}
          </div>
          <InvestmentChartSkeleton />
        </>
      ) : (
        <>
          <PortfolioOverview
            data={balanceData}
            onMakePayment={handleMakePayment}
            liveBtcPrice={liveBtcPrice}
          />
          <InvestmentChart
            data={balanceData?.chartData || {}}
            onMakePayment={handleMakePayment}
          />
        </>
      )}

      {isTransactionsLoading ? (
        <TransactionTableSkeleton />
      ) : (
        <TransactionTable
          transactions={transactionsData || []}
          onMakePayment={handleMakePayment}
        />
      )}

      <a href="/faq" className="dash-help-card">
        <div className="dash-help-icon" aria-hidden="true">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M5 4h14a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H9l-4 4V6a2 2 0 0 1 2-2z" />
            <path d="M9.2 9.2a2.8 2.8 0 0 1 5.2 1.4c0 1.8-2.4 2.1-2.4 3.6" />
            <circle cx="12" cy="16.6" r="0.6" fill="currentColor" stroke="none" />
          </svg>
        </div>
        <div className="dash-help-text">
          <div className="dash-help-title">Questions about how it works?</div>
          <div className="dash-help-sub">
            Payment windows, fees, exchange rates, withdrawals and more — find
            answers in the Help Center.
          </div>
        </div>
        <span className="dash-help-cta">
          Visit Help &amp; FAQ <span className="dash-help-arrow">→</span>
        </span>
      </a>

      <PaymentModal
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
        onPaymentSuccess={fetchDashboardData}
      />
    </div>
  )
}

export default DashboardScreen
